import { ChangeDetectionStrategy, Component, Input, numberAttribute, OnChanges } from '@angular/core';

import { CurrencyPipe } from '../../../../pipes/currency.pipe';
import { NumberService } from '../../../../services/number/number.service';
import { TranslatePipe } from '../../../../pipes/translate.pipe';

export type RangeType = 'day' | 'year';

/**
 *
 * This component displays a min value on the left side, a max value on the right side
 * and it indicates WHERE in the range the value occurs.
 *
 *  ========== PLOTTING  DATA ==========
 *
 * There are two usages of this directive:
 *
 * 1) When you know the percentage of value use the 'percentage' attribute:
 *
 * <td-wb-range-indicator [min]="0" [max]="100"
 *                                [percentage]="25"></td-wb-markets-range-indicator>
 *
 * This will display a green indicator bar that is 25% of the width of the range-indicator directive.
 *
 *
 * 2) When you DO NOT know the percentage of value use the 'value' attribute:
 *
 * <td-wb-range-indicator [min]="50" [max]="100"
 *                                [value]="75"></td-wb-markets-range-indicator>
 *
 * The directive will calculate 75 as 50% of the range of values and will
 * display a green indicator bar that is 50% of the width of the range-indicator directive.
 *
 * ========== DISPLAYING OPTIONAL LABELS ==========
 *
 * This directive can optionally display a label to indicate the rangeType of 'Day Range' or 'Year Range'.
 *
 * <td-wb-range-indicator [type]="'day'"></td-wb-markets-range-indicator>
 * <td-wb-range-indicator [type]="'year'"></td-wb-markets-range-indicator>
 *
 * The copy for these labels are defined in range-indicator-locale.js.
 */
@Component({
  selector: 'td-wb-markets-range-indicator',
  templateUrl: 'range-indicator.component.html',
  styleUrls: ['range-indicator.component.scss'],
  imports: [
    TranslatePipe,
    CurrencyPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RangeIndicatorComponent implements OnChanges {
  /* eslint-disable lines-between-class-members */
  /**
   * Input getter and setter
   * Convert to number using TypeConverter
   */
  @Input()
  get min(): number { return this._min; }
  set min(value: number) {
    this._min = numberAttribute(value, undefined);
  }
  @Input()
  get max(): number { return this._max; }
  set max(value: number) {
    this._max = numberAttribute(value, undefined);
  }
  @Input()
  get value(): number { return this._value; }
  set value(value: number) {
    this._value = numberAttribute(value, undefined);
  }
  @Input()
  get percentage(): number { return this._percentage; }
  set percentage(value: number) {
    this._percentage = numberAttribute(value, undefined);
  }
  @Input() type: RangeType; // no label when undefined
  /* eslint-enable lines-between-class-members */

  width: number;
  label: string;
  private _min: number;
  private _max: number;
  private _value: number;
  private _percentage: number;

  /**
   * Setup is done in onChanges instead of OnInit
   * so that component can handle input changes
   */
  ngOnChanges(): void {
    this.setMinMax();
    this.width = this.getPercentage();
    this.label = this.getLabel();
  }

  /**
   * Adjust min and max according to value
   */
  private setMinMax(): void {
    if (this.value !== undefined) {
      this.min = this.min > this.value ? this.value : this.min;
      this.max = this.max < this.value ? this.value : this.max;
    }
  }

  /**
   * Calculate and return percentage used for width of component
   * The consumer has specified the percentage of value, use it directly
   * The consumer has given us three values(min, max & value),
   * we need to calculate the percentageOfValue
   *
   * @returns
   */
  private getPercentage(): number {
    if (this.percentage !== undefined) {
      return this.percentage;
    }
    const distanceApart = this.max - this.min;
    const remainder = this.value - this.min;
    const percentageTemp = ((remainder / distanceApart) * 100);
    return NumberService.round(percentageTemp);
  }

  /**
   * Determine if range label is: DAY | YEAR | NONE
   *
   * @returns value to display
   */
  private getLabel(): string | undefined {
    if (this.type === 'day' || this.type === 'year') {
      return this.type.toUpperCase() + '_RANGE';
    }
    return undefined;
  }
}
